import { createSelector } from 'reselect';
import {
  get,
  omit,
  findIndex,
  forEach,
  isEmpty,
  uniq,
  flatMap,
  find,
  maxBy,
  isNumber,
  noop,
  groupBy,
  mergeWith,
} from 'lodash';

import {
  GROUP_AB_TEST,
  GROUP_CONJOINT,
  GROUP_VAN_WESTENDORP,
  GROUP_MAXDIFF,
} from 'data/nodeTypes';
import { QUOTA_MODE } from 'data/quotaModes';

import {
  isAbGroup,
  isConjointGroup,
  isMaxDiffGroup,
  isConjointOrMaxDiffGroup,
  isVanWestendorpGroup,
  isLoopAndMergeGroup,
  isMatrixSingleOrMultipleNode,
  isAbGroupNode,
  isConjointGroupNode,
  isMaxDiffGroupNode,
  isLoopAndMergeGroupNode,
  isConstantSumNode,
  isConversationalNode,
  isConjointOrMaxDiffGroupNode,
  isVanWestendorpQuestionNode,
  isVanWestendorpGroupNode,
} from 'utils/nodeTypes';

import { MATRIX_CONSTANT_SUM_TYPES } from 'containers/QuestionnairePage/GenericQuestion/MatrixOpenEndedQuestion/constants';
import { SHUFFLE_NONE } from 'components/DropdownShuffle/constants';
import { transformNodesForTranslations } from 'containers/SurveyTranslationPage/utils';
import {
  selectSurveyTranslations,
  selectSurveySelectedLanguage,
  makeSelectTranslateOpenEnded,
} from 'containers/SurveyTranslationPage/selectors';
import {
  makeSelectIsDistributionLink,
  makeSurveyIsDraft,
  makeSurveyIsUnderEdit,
} from 'containers/Survey/selectors';
import {
  makeSelectAccountPerks,
  makeSelectHasPerk,
} from 'containers/User/selectors';
import { makeSelectIsPayingPlan } from 'containers/SubscriptionPlans/selectors';
import { makeSelectHiddenVariables } from 'modules/HiddenVariables/selectors';
import {
  questionnaireLogicService,
  VALIDATION_ERRORS,
  VALIDATION_SCOPES,
} from 'utils/adl-services';

import { QUESTION_LIMIT, QUESTION_LIMIT_DISTRIBUTION_LINK } from './constants';

import {
  getNodeByRefId,
  hasMentionOnRefId,
  isThereAnyPreviousQuestionOnNodesList,
  questionHasAnswersInPredefinedTarget,
  hasNodeMentionsOnAnswers,
  enrichNodeForMentions,
  hasQuestionQuotasAdvanced,
  hasAnswerLimitsEnabled,
  getQuestionQuotaMode,
} from './utils';
import { getFormattedNodes } from './selectorUtils';
import { shouldEnforceScreeningBoundary } from './screeningBoundaryUtils';
import {
  hasPerkConversational,
  hasPerkSurveyTheming,
} from '../Permissions/helpers';
import makeSelectActiveTheme from '../SurveyTheme/selectors';
import { MAX_CONVERSATIONAL_AI_QUESTIONS } from './GenericQuestion/ConversationalQuestion/constants';
import { isQuestionUsedInLoopAndMerge } from './NodeSettings/settings/utils/CarryForward';
import {
  makeSelectQuestionQuotasMinimal,
  makeSelectQuestionQuotasMinimalMap,
} from './selectorUtils/quotasMinimal';
import { getAdlBlockingErrorSections } from './selectorUtils/adlErrorSections';

/**
 * Direct selector to the QuestionnairePage state domain
 */
const selectQuestionnairePageDomain = (state) =>
  get(state, 'questionnairePage', {});
const selectQuestionnaireLogicDomain = (state) =>
  get(state, 'questionnairePage.questionnaireLogic', {});
const selectQuestionnaireDisqualificationLogic = (state) =>
  get(state, 'questionnairePage.disqualificationLogic', {});

const selectQuestionnaireCommentsDomain = (state) =>
  get(state, 'surveyComments');

const selectQuestionnaireActiveQuestionId = (state) =>
  get(state, 'questionnairePage.activeQuestionId');
const selectQuestionnaireActiveGroupId = (state) =>
  get(state, 'questionnairePage.activeGroupId');
const selectQuestionsDiff = (state) =>
  get(state, 'questionnairePage.questionsDiff');

const selectScreeningQuestions = (state) =>
  get(state, 'questionnairePage.surveyDetailsExtended.screeningQuestions', []);
const selectQuestionnaireQuestions = (state) =>
  get(state, 'questionnairePage.surveyDetailsExtended.questions', []);
const selectQuestionnaireGroups = (state) =>
  get(state, 'questionnairePage.surveyDetailsExtended.questionGroups', []);

// WARNING: this should be a questionnaireLogic selector but it creates circular dependencies,
// we need to check and fix this appropriately
const selectQuestionnaireLogicPaths = (state) =>
  get(state, 'questionnairePage.questionnaireLogic.logicPaths');
const selectQuestionnaireLogicPathsWithoutScreeningNavigation = (state) =>
  get(
    state,
    'questionnairePage.questionnaireLogic.logicPathsWithoutScreeningNavigation',
  );
const selectQuestionnaireLogicErrors = (state) =>
  get(state, 'questionnairePage.questionnaireLogic.errors');
const selectNodesWithLogic = (state) =>
  get(state, 'questionnairePage.questionnaireLogic.nodesWithLogic');
const selectNodesWithNonScreeningLogic = (state) =>
  get(state, 'questionnairePage.questionnaireLogic.nodesWithNonScreeningLogic');
const selectNodesWithLogicFromOutcomesShow = (state) =>
  get(
    state,
    'questionnairePage.questionnaireLogic.nodesWithLogicFromOutcomesShow',
  );
const selectNodesWithQuotaOnlyLogic = (state) => ({
  nodesWithSimpleQuotaOnlyLogic: get(
    state,
    'questionnairePage.questionnaireLogic.nodesWithSimpleQuotaOnlyLogic',
  ),
  nodesWithAdvancedQuotaOnlyLogic: get(
    state,
    'questionnairePage.questionnaireLogic.nodesWithAdvancedQuotaOnlyLogic',
  ),
});

const selectTaxonomyWithQuestionnaireLogic = (state) =>
  get(state, 'questionnairePage.questionnaireLogic.taxonomyWithLogic');
const selectAnswersWithLogic = (state) =>
  get(state, 'questionnairePage.questionnaireLogic.answersWithLogic');
const selectAnswersWithNonScreeningLogic = (state) =>
  get(
    state,
    'questionnairePage.questionnaireLogic.answersWithNonScreeningLogic',
  );
const selectAnswersWithLogicFromOutcomes = (state) =>
  get(
    state,
    'questionnairePage.questionnaireLogic.answersWithLogicFromOutcomes',
  );
const selectAnswersWithLogicFromOutcomesShow = (state) =>
  get(
    state,
    'questionnairePage.questionnaireLogic.answersWithLogicFromOutcomesShow',
  );
const selectAnswersWithLogicFromOutcomesHide = (state) =>
  get(
    state,
    'questionnairePage.questionnaireLogic.answersWithLogicFromOutcomesHide',
  );
const selectQuestionnaireLogicHiddenNodes = (state) =>
  get(state, 'questionnairePage.questionnaireLogic.hiddenNodes', []);
const selectAnswersWithDisqualificationLogic = (state) =>
  get(state, 'questionnairePage.disqualificationLogic.answersWithLogic');
const selectConceptsWithLogic = (state) =>
  get(state, 'questionnairePage.questionnaireLogic.conceptsWithLogic');
const selectNodesWithDisqualificationLogic = (state) =>
  get(state, 'questionnairePage.disqualificationLogic.nodesWithLogic');
const selectTaxonomyWithDisqualificationLogic = (state) =>
  get(state, 'questionnairePage.disqualificationLogic.taxonomyWithLogic');
const selectVariablesWithLogic = (state) =>
  get(state, 'questionnairePage.questionnaireLogic.variablesWithLogic');
const selectVariablesWithDisqualificationLogic = (state) =>
  get(state, 'questionnairePage.disqualificationLogic.variablesWithLogic');

/**
 * Other specific selectors
 */

/**
 * Default selector used by QuestionnairePage
 */

const makeSelectQuestionnairePage = createSelector(
  selectQuestionnairePageDomain,
  (substate) => substate,
);

const makeSelectQuestionnaireRevisions = createSelector(
  selectQuestionnairePageDomain,
  (substate) => get(substate, 'revisions', {}),
);

const makeSelectIsQuestionnaireInRevisionPreviewMode = createSelector(
  selectQuestionnairePageDomain,
  (substate) => get(substate, 'revisions.isInRevisionPreviewMode', false),
);

const makeSelectQuestionnaireLoading = createSelector(
  selectQuestionnairePageDomain,
  (substate) => get(substate, 'isFetching'),
);

const makeSelectHasFetched = createSelector(
  selectQuestionnairePageDomain,
  (substate) => get(substate, 'hasFetched'),
);

const makeSelectIsSaving = createSelector(
  makeSelectQuestionnairePage,
  (substate) => substate.isSaving,
);

const makeSelectIsFetchingConjointSettings = createSelector(
  selectQuestionnairePageDomain,
  (substate) => get(substate, 'isFetchingDoeSettingsData'),
);

const makeSelectQuestionnaireSaving = createSelector(
  makeSelectIsSaving,
  makeSelectIsFetchingConjointSettings,
  (isQuestionnaireSaving, isFetchingSettings) =>
    isQuestionnaireSaving ||
    // not sure this should trigger a "questionnaireSaving" flag
    // we should probably change the messaging
    isFetchingSettings,
);

const makeSelectRecallRequest = createSelector(
  selectQuestionnairePageDomain,
  (substate) => get(substate, 'insertRecallRequest', {}),
);

const makeSelectQuestionnaireEditVersion = createSelector(
  selectQuestionnairePageDomain,
  (substate) =>
    get(substate, 'surveyDetailsExtended.questionnaireEditVersion', -1),
);
const makeSelectQuestionnaireSavedEditVersion = createSelector(
  selectQuestionnairePageDomain,
  (substate) => get(substate, 'lastSuccessfulVersion', -1),
);

const makeSelectNodesWithLogic = createSelector(
  selectNodesWithLogic,
  selectNodesWithDisqualificationLogic,
  (nodesWithLogic = [], nodesWithDisqualificationLogic = []) =>
    uniq([...nodesWithLogic, ...nodesWithDisqualificationLogic]),
);

const makeSelectNodesWithNonScreeningLogic = createSelector(
  selectNodesWithNonScreeningLogic,
  selectNodesWithDisqualificationLogic,
  (nodesWithNonScreeningLogic = [], nodesWithDisqualificationLogic = []) =>
    uniq([...nodesWithNonScreeningLogic, ...nodesWithDisqualificationLogic]),
);

const makeSelectHiddenNodes = createSelector(
  selectQuestionnaireLogicHiddenNodes,
  (substate) => substate,
);

const makeSelectNodesWithLogicFromOutcomesShow = createSelector(
  selectNodesWithLogicFromOutcomesShow,
  (nodesWithLogic = []) => nodesWithLogic,
);
const makeSelectNodesWithSimpleQuotaOnlyLogic = createSelector(
  selectNodesWithQuotaOnlyLogic,
  ({ nodesWithSimpleQuotaOnlyLogic } = []) => nodesWithSimpleQuotaOnlyLogic,
);
const makeSelectNodesWithAdvancedQuotaOnlyLogic = createSelector(
  selectNodesWithQuotaOnlyLogic,
  ({ nodesWithAdvancedQuotaOnlyLogic } = []) => nodesWithAdvancedQuotaOnlyLogic,
);

const makeSelectTaxonomyWithQuestionnaireLogic = createSelector(
  selectTaxonomyWithQuestionnaireLogic,
  (taxonomyWithQuestionnaireLogic = {}) => taxonomyWithQuestionnaireLogic,
);

const makeSelectTaxonomyWithLogic = createSelector(
  makeSelectTaxonomyWithQuestionnaireLogic,
  selectTaxonomyWithDisqualificationLogic,
  (taxonomyWithQuestionnaireLogic, taxonomyWithDisqualificationLogic = {}) => ({
    taxonomyWithQuestionnaireLogic,
    taxonomyWithDisqualificationLogic,
  }),
);

const makeSelectMaxQlPath = createSelector(
  selectQuestionnaireLogicDomain,
  (substate) => get(substate, 'maxQlPath', 0),
);

const makeSelectAnswersWithOriginLogic = createSelector(
  selectAnswersWithLogic,
  selectQuestionnairePageDomain,
  (answersWithLogic, questionnaire) => {
    if (!answersWithLogic) {
      return [];
    }
    const questions = get(questionnaire, 'surveyDetailsExtended.questions', []);
    let questionIndex;
    const answersWithOriginLogic = new Array(answersWithLogic.length).fill({});

    forEach(questions, (question) => {
      const answersProp = isMatrixSingleOrMultipleNode(question)
        ? 'scorepoints'
        : 'answers';

      forEach(question[answersProp], (answerToCheck) => {
        questionIndex = -1;

        if (answersProp === 'answers') {
          questionIndex = findIndex(
            answersWithLogic,
            (answerRefId) => answerRefId === answerToCheck.refId,
          );
        } else {
          questionIndex = findIndex(answersWithLogic, [
            'row',
            answerToCheck.refId,
          ]);
        }

        if (questionIndex > -1) {
          answersWithOriginLogic[questionIndex] = {
            value: answersWithLogic[questionIndex],
            originalAnswerRefId: answerToCheck.originalAnswerRefId || null,
          };
        }
      });
    });

    return answersWithOriginLogic;
  },
);

const makeSelectQuestionLimit = createSelector(
  makeSelectIsDistributionLink,
  (isDistributionLink) =>
    isDistributionLink ? QUESTION_LIMIT_DISTRIBUTION_LINK : QUESTION_LIMIT,
);

const makeSelectHasTooManyQuestions = createSelector(
  makeSelectMaxQlPath,
  makeSelectQuestionLimit,
  (maxQLPath, questionLimit) => maxQLPath > questionLimit,
);

const makeSelectHasReachedQuestionLimit = createSelector(
  makeSelectMaxQlPath,
  makeSelectQuestionLimit,
  (maxQLPath, questionLimit) => maxQLPath >= questionLimit,
);

const makeSelectAnswersWithLogic = createSelector(
  selectAnswersWithLogic,
  selectAnswersWithDisqualificationLogic,
  (answersWithLogic = [], answersWithDisqualificationLogic = []) =>
    uniq([...answersWithLogic, ...answersWithDisqualificationLogic]),
);

const makeSelectAnswersWithNonScreeningLogic = createSelector(
  selectAnswersWithNonScreeningLogic,
  selectAnswersWithDisqualificationLogic,
  (answersWithNonScreeningLogic = [], answersWithDisqualificationLogic = []) =>
    uniq([
      ...answersWithNonScreeningLogic,
      ...answersWithDisqualificationLogic,
    ]),
);

const makeSelectAnswersWithLogicFromOutcomes = createSelector(
  selectAnswersWithLogicFromOutcomes,
  (answersWithLogic = []) => answersWithLogic,
);

const makeSelectAnswersWithLogicFromOutcomesShow = createSelector(
  selectAnswersWithLogicFromOutcomesShow,
  (answersWithLogic = []) => answersWithLogic,
);

const makeSelectAnswersWithLogicFromOutcomesHide = createSelector(
  selectAnswersWithLogicFromOutcomesHide,
  (answersWithLogic = []) => answersWithLogic,
);

const makeSelectConceptsWithLogic = createSelector(
  selectConceptsWithLogic,
  (substate) => substate,
);

const makeSelectQuestionnaireGroups = createSelector(
  selectQuestionnaireGroups,
  (groups) => groups,
);

const makeSelectScreeningLogicEnabled = createSelector(
  selectQuestionnairePageDomain,
  (questionnaire) => get(questionnaire, 'screeningLogicEnabled', false),
);

const makeSelectNonScreeningQuestions = createSelector(
  makeSelectScreeningLogicEnabled,
  selectQuestionnaireQuestions,
  (screeningLogicEnabled, questions = []) =>
    screeningLogicEnabled
      ? questions.filter((question) => !question.isScreening)
      : questions,
);

const makeSelectVariableWithLogicActionsPerNode = createSelector(
  selectVariablesWithLogic,
  (variablesWithLogic = {}) => variablesWithLogic.actionsPerNode,
);

const makeSelectScreeningQuestionsInternal = createSelector(
  makeSelectScreeningLogicEnabled,
  selectScreeningQuestions,
  selectQuestionnaireQuestions,
  (screeningLogicEnabled, screeningQuestions = [], questions = []) =>
    screeningLogicEnabled
      ? questions.filter((question) => question.isScreening)
      : screeningQuestions,
);

const makeSelectMaxAllowedScreeningQuestions = createSelector(
  makeSelectHasPerk('extraScreeningQuestions'),
  (hasExtraScreenerPerk) => (hasExtraScreenerPerk ? 6 : 3),
);

const makeSelectScreeningQuestionsLength = createSelector(
  makeSelectScreeningQuestionsInternal,
  (screeningQuestions) => screeningQuestions.length,
);

const makeSelectCanAddMoreScreeningQuestions = createSelector(
  makeSelectMaxAllowedScreeningQuestions,
  makeSelectScreeningQuestionsLength,
  (maxAllowedScreeningQuestions, numberOfScreeningQuestions) =>
    numberOfScreeningQuestions < maxAllowedScreeningQuestions,
);

const makeSelectShouldEnforceScreeningBoundary = createSelector(
  makeSelectScreeningLogicEnabled,
  selectQuestionnaireQuestions,
  (screeningLogicEnabled, questions = []) =>
    shouldEnforceScreeningBoundary({ screeningLogicEnabled, questions }),
);

const makeSelectScreeningQuestions = createSelector(
  makeSelectScreeningQuestionsInternal,
  noop,
  noop,
  selectQuestionnaireActiveQuestionId,
  selectQuestionnaireCommentsDomain,
  makeSelectVariableWithLogicActionsPerNode,
  makeSelectHiddenVariables,
  makeSelectCanAddMoreScreeningQuestions,
  makeSelectHiddenNodes,
  makeSelectShouldEnforceScreeningBoundary,
  (
    screeningQuestions,
    _logicPaths,
    _logicErrors,
    activeQuestionId,
    surveyComments,
    variableActionsPerNode,
    hiddenVariables,
    canAddScreeningQuestions,
    hiddenNodes,
    enforceScreeningBoundary,
  ) =>
    getFormattedNodes(
      screeningQuestions,
      _logicPaths,
      _logicErrors,
      activeQuestionId,
      surveyComments,
      variableActionsPerNode,
      hiddenVariables,
      canAddScreeningQuestions,
      hiddenNodes,
      enforceScreeningBoundary,
    ),
);

// when screening logic is enabled, screening questions are already part of
// the unified questions list; only legacy surveys keep them outside it
const makeSelectScreeningQuestionsOutsideQuestionsList = createSelector(
  makeSelectScreeningLogicEnabled,
  makeSelectScreeningQuestions,
  (screeningLogicEnabled, screeningQuestions = []) =>
    screeningLogicEnabled ? [] : screeningQuestions,
);

const makeSelectLogicErrors = createSelector(
  selectQuestionnaireLogicErrors,
  (substate) => substate || [],
);

const makeSelectLogicPaths = createSelector(
  selectQuestionnaireLogicPaths,
  (substate) => substate,
);

const makeSelectLogicPathsWithoutScreeningNavigation = createSelector(
  selectQuestionnaireLogicPathsWithoutScreeningNavigation,
  selectQuestionnaireLogicPaths,
  (logicPathsWithoutScreeningNavigation, logicPaths) =>
    logicPathsWithoutScreeningNavigation ?? logicPaths,
);

const makeSelectRulesWithQuotas = createSelector(
  selectQuestionnaireLogicDomain,
  (substate) => get(substate, 'rulesWithQuotas', []),
);

const makeSelectLogicErrorsExtended = createSelector(
  makeSelectLogicErrors,
  (errors = []) => {
    const groupedErrors = groupBy(errors, 'code');
    const circleErrors = groupedErrors[VALIDATION_ERRORS.CIRCLE];
    const unusedNodes = groupedErrors[VALIDATION_ERRORS.UNUSED_NODES];
    const wrongContextReferenceErrors =
      groupedErrors[VALIDATION_ERRORS.WRONG_CONTEXT_REFERENCE] || [];

    const invalidReferences = wrongContextReferenceErrors.reduce(
      (accumulator, { scope, nodeId, details }) => {
        if (!accumulator[scope][nodeId]) {
          accumulator[scope][nodeId] = [];
        }

        accumulator[scope][nodeId].push(details.unansweredNode);

        return accumulator;
      },
      {
        [VALIDATION_SCOPES.GRAPH]: {},
        [VALIDATION_SCOPES.ANSWER]: {},
        [VALIDATION_SCOPES.NODE]: {},
        [VALIDATION_SCOPES.VARIABLE]: {},
      },
    );

    return {
      errors,
      unusedNodes: unusedNodes?.[0].details,
      invalidReferences: invalidReferences[VALIDATION_SCOPES.GRAPH],
      invalidReferencesVariablesLogic:
        invalidReferences[VALIDATION_SCOPES.VARIABLE],
      invalidReferencesAnswersLogic: mergeWith(
        {},
        invalidReferences[VALIDATION_SCOPES.NODE],
        invalidReferences[VALIDATION_SCOPES.ANSWER],
        (nodeValue = [], answerValue = []) => nodeValue.concat(answerValue),
      ),
      circleErrors,
    };
  },
);

// Nodes whose rules reference an unconditionally hidden answer
// (kept with "Keep rules"); a blocking error until the rules are updated.
const makeSelectNodesWithHiddenAnswerReferences = createSelector(
  makeSelectLogicErrors,
  (errors = []) =>
    uniq(
      errors
        .filter(
          ({ code }) => code === VALIDATION_ERRORS.HIDDEN_ANSWER_REFERENCE,
        )
        .map(({ nodeId }) => nodeId),
    ),
);

const makeSelectAdlBlockingErrorSections = createSelector(
  makeSelectLogicErrors,
  (state) => get(state, 'questionnairePage.questionnaireLogic.ql'),
  (errors, ql) => getAdlBlockingErrorSections(errors, ql),
);

const makeSelectExtendedSurveyDetails = createSelector(
  selectQuestionnaireQuestions,
  makeSelectLogicPathsWithoutScreeningNavigation,
  makeSelectLogicErrorsExtended,
  selectQuestionnaireActiveQuestionId,
  selectQuestionnaireCommentsDomain,
  makeSelectVariableWithLogicActionsPerNode,
  makeSelectHiddenVariables,
  makeSelectCanAddMoreScreeningQuestions,
  makeSelectHiddenNodes,
  makeSelectShouldEnforceScreeningBoundary,
  (
    questions,
    logicPathsWithoutScreeningNavigation,
    logicErrors,
    activeQuestionId,
    surveyComments,
    variableActionsPerNode,
    hiddenVariables,
    canAddScreeningQuestions,
    hiddenNodes,
    enforceScreeningBoundary,
  ) =>
    getFormattedNodes(
      questions,
      logicPathsWithoutScreeningNavigation,
      logicErrors,
      activeQuestionId,
      surveyComments,
      variableActionsPerNode,
      hiddenVariables,
      canAddScreeningQuestions,
      hiddenNodes,
      enforceScreeningBoundary,
    ),
);

const makeSelectSurveyLogicToStore = createSelector(
  selectQuestionnaireLogicDomain,
  (substate) => {
    // the backend requested we add the "refId" names on the properties to avoid confusion
    // On the other hand, the library doesnt care much if the id provided is a refId or not
    // as long as its a string. I am not sure i'll keep it this way but for now this is needed
    const questions = get(substate, 'basicLogicMap.questions', []);
    const ql = get(substate, 'ql', {});
    const backendExpectedLogicQuestions = questions.map((question) => ({
      refId: question.id,
      nextQuestionRefId: question.nextQuestionRefId,
      answers: question.answers.map((answer) => ({
        refId: answer.id,
        nextQuestionRefId: answer.nextQuestion,
      })),
    }));

    return {
      logic: { questions: backendExpectedLogicQuestions },
      ql: questionnaireLogicService.cleanFromEmptyValues(ql),
      error: substate.error,
    };
  },
);
const makeSelectActiveQuestionId = createSelector(
  selectQuestionnaireActiveQuestionId,
  (substate) => substate,
);

const makeSelectPermissionsForScreeningQuestions = createSelector(
  makeSelectScreeningLogicEnabled,
  makeSelectScreeningQuestionsLength,
  makeSelectMaxAllowedScreeningQuestions,
  makeSelectIsPayingPlan,
  makeSurveyIsUnderEdit,
  (
    screeningLogicEnabled,
    numberOfScreeningQuestions,
    maxAllowedQuestions,
    hasPayingPlan,
    isUnderEdit,
  ) => {
    const canAdd = numberOfScreeningQuestions < maxAllowedQuestions;
    const hasExceedMaxAllowed =
      numberOfScreeningQuestions > maxAllowedQuestions;
    const canAllowMoreScreeningQuestions =
      !canAdd && !hasExceedMaxAllowed && maxAllowedQuestions === 3;

    return {
      canShowMenu: !isUnderEdit && screeningLogicEnabled,
      canAdd,
      planFeature: canAllowMoreScreeningQuestions && !hasPayingPlan,
      contactManager: canAllowMoreScreeningQuestions && hasPayingPlan,
      maxAllowedQuestions,
      numberOfScreeningQuestions,
      hasPayingPlan,
      hasExceedMaxAllowed,
    };
  },
);

const makeSelectFormattedNodes = createSelector(
  makeSelectExtendedSurveyDetails,
  (questions = []) =>
    questions.map((question, index) => ({
      ...question,
      index,
    })),
);

const makeSelectActiveQuestionInternal = createSelector(
  selectQuestionnaireActiveQuestionId,
  selectQuestionnaireActiveGroupId,
  makeSelectFormattedNodes,
  (activeQuestionId, activeGroupId, nodes = []) =>
    getNodeByRefId(nodes, activeQuestionId, activeGroupId),
);

const makeSelectActiveQuestion = createSelector(
  makeSelectActiveQuestionInternal,
  (activeQuestion) => activeQuestion,
);

const makeSelectActiveQuestionHasAdvancedQuotas = createSelector(
  makeSelectActiveQuestionInternal,
  makeSelectQuestionQuotasMinimal,
  (activeQuestion, quotasMinimalMap) => {
    if (!activeQuestion) return false;
    const activeQuestionRefId = get(activeQuestion, 'refId');

    return hasQuestionQuotasAdvanced({
      refId: activeQuestionRefId,
      quotasMinimalMap,
    });
  },
);

// used for settings views helps with rerenders
const makeSelectActiveQuestionSettings = createSelector(
  makeSelectActiveQuestionInternal,
  makeSelectExtendedSurveyDetails,
  makeSelectQuestionQuotasMinimalMap,
  (activeQuestion, nodes = [], quotasMinimalMap) => {
    if (!activeQuestion) return {};

    const quotaMode = getQuestionQuotaMode({
      refId: activeQuestion.refId,
      quotasMinimalMap,
    });
    const isQuotasEnabled = quotaMode === QUOTA_MODE.MAX;
    const isMinimumResponsesEnabled = quotaMode === QUOTA_MODE.MIN;

    const { carryForward: activeQuestionCarryForward = [], groupRefId } =
      activeQuestion;
    let canEnableCarryForward = false;
    let hasConstantSumMinMax = false;
    let canEnableMinMax = true;
    // CF not enable on questions that belong to group
    if (!groupRefId) {
      const nodesList = [...nodes];
      canEnableCarryForward =
        !isQuotasEnabled &&
        !isMinimumResponsesEnabled &&
        (!isEmpty(activeQuestionCarryForward) ||
          isThereAnyPreviousQuestionOnNodesList(
            nodesList,
            activeQuestion.refId,
          )) &&
        !isQuestionUsedInLoopAndMerge(activeQuestion.refId, nodesList);

      if (isConstantSumNode(activeQuestion)) {
        const validation = get(
          activeQuestion,
          'matrix.validation.openEndedValidation',
          {},
        );
        const config = get(activeQuestion, 'matrix.openEndedConfig');

        if (config?.gridType === MATRIX_CONSTANT_SUM_TYPES.MUST_TOTAL) {
          if (isNumber(validation.min) || isNumber(validation.max)) {
            canEnableCarryForward = false;
            hasConstantSumMinMax = true;
          } else if (!isEmpty(activeQuestionCarryForward)) {
            canEnableMinMax = false;
          }
        }
      }
    }

    const canEnableAnswerLimits = !isMinimumResponsesEnabled;

    let hasParentNodeWithEnabledShuffling = false;
    let isInAb = false;
    let isInLoopAndMerge = false;
    let isInVanWestendorpGroup = false;
    const group = groupRefId ? getNodeByRefId(nodes, groupRefId) : undefined;
    if (group) {
      hasParentNodeWithEnabledShuffling =
        isVanWestendorpGroup(group.type) ||
        (!!group.shuffle && group.shuffle !== SHUFFLE_NONE);

      isInAb = isAbGroup(group.type);
      isInLoopAndMerge = isLoopAndMergeGroup(group.type);
      isInVanWestendorpGroup = isVanWestendorpGroup(group.type);
    }

    const abAttributesList =
      group && isAbGroup(group.type)
        ? group.attributes.map(({ refId, name }) => ({
            refId,
            name,
          }))
        : undefined;

    const hasAnswersInPredefinedTarget =
      questionHasAnswersInPredefinedTarget(activeQuestion);

    const canBatchAnswers = !hasNodeMentionsOnAnswers(activeQuestion);

    return omit(
      {
        ...activeQuestion,
        canEnableCarryForward,
        canEnableMinMax,
        canEnableAnswerLimits,
        hasConstantSumMinMax,
        isMinimumResponsesEnabled,
        abAttributesList,
        hasAnswersInPredefinedTarget,
        hasParentNodeWithEnabledShuffling,
        isInAb,
        isInLoopAndMerge,
        isInVanWestendorpGroup,
        canBatchAnswers,
        hasAnswerLimitsEnabled: hasAnswerLimitsEnabled(activeQuestion),
      },
      ['ignoreScrollIfInViewport', 'scrollIntoView', 'questions'],
    );
  },
);

const makeSelectNodesForTranslations = createSelector(
  makeSelectExtendedSurveyDetails,
  (allNodes = []) => {
    // screening questions are rendered as a separate SQ-numbered section on
    // the translations page, so only the survey body belongs here
    const nodes = allNodes.filter((node) => !node.isScreening);

    if (isEmpty(nodes)) {
      return [];
    }

    const nodesForTranslations = [];

    let questionIndex = 0;
    nodes.forEach((questionOrGroup) => {
      questionIndex += 1;
      const hasSubQuestions = !!questionOrGroup.questions;

      if (!isConjointOrMaxDiffGroup(questionOrGroup.type)) {
        nodesForTranslations.push({ ...questionOrGroup, questionIndex });
        if (questionOrGroup.questions) {
          questionIndex += questionOrGroup.questions.length - 1;
        }
        if (!hasSubQuestions) {
          return;
        }
      }

      const group = questionOrGroup;
      // we need to show conjoint or maxdiff groups as groups for translations page
      if (isConjointOrMaxDiffGroup(group.type)) {
        nodesForTranslations.push({ ...group, questionIndex });
      }
    });

    return nodesForTranslations;
  },
);

const makeSelectTranslatedNodes = createSelector(
  selectSurveySelectedLanguage,
  selectSurveyTranslations,
  makeSelectNodesForTranslations,
  makeSelectScreeningQuestions,
  makeSelectTranslateOpenEnded,
  (
    selectedLanguage,
    translations,
    nodes = [],
    screeningQuestions = [],
    translateOpenEnded,
  ) => {
    if (!selectedLanguage) {
      return {
        nodes: [],
        screeningQuestions: [],
      };
    }

    const translatedNodes = transformNodesForTranslations(
      nodes,
      translations,
      selectedLanguage,
      translateOpenEnded,
    );
    const translatedScreeningQuestions = transformNodesForTranslations(
      screeningQuestions,
      translations,
      selectedLanguage,
      translateOpenEnded,
    );

    return {
      nodes: translatedNodes,
      screeningQuestions: translatedScreeningQuestions,
    };
  },
);

const makeSelectTranslatedQuestions = createSelector(
  makeSelectTranslatedNodes,
  ({ nodes = [], screeningQuestions = [] }) => {
    const questions = nodes.reduce(
      (accumulator, node) => [...accumulator, node],
      [],
    );

    return { questions, screeningQuestions };
  },
);

const makeSelectQuestionsForMentions = createSelector(
  makeSelectExtendedSurveyDetails,
  makeSelectScreeningQuestionsOutsideQuestionsList,
  makeSelectTranslatedQuestions,
  (questions = [], outsideListScreeners = [], translated) => {
    const {
      questions: translatedQuestions,
      screeningQuestions: translatedScreeningQuestions,
    } = translated;
    const hasTranslations = translatedQuestions.length > 0;
    if (hasTranslations) {
      let index = 0;

      return [...translatedQuestions, ...translatedScreeningQuestions].reduce(
        (accumulator, question) => {
          const { refId, translatedRefId, questions: subNodes = [] } = question;
          const propertyName = question.isScreening
            ? 'screeningQuestions'
            : 'questions';

          index += 1;
          accumulator[propertyName][refId] = enrichNodeForMentions(
            question,
            index,
            true,
          );

          if (translatedRefId) {
            accumulator.translations[translatedRefId] = refId;
          }

          if (subNodes.length) {
            subNodes.forEach((subNode) => {
              const {
                refId: subNodeRefId,
                translatedRefId: translatedSubRefId,
              } = subNode;
              index += 1;

              accumulator.questions[subNodeRefId] = enrichNodeForMentions(
                subNode,
                index,
                true,
              );

              if (translatedSubRefId) {
                accumulator.translations[translatedSubRefId] = subNodeRefId;
              }
            });
          }

          return accumulator;
        },
        { questions: {}, screeningQuestions: {}, translations: {} },
      );
    }

    let index = 0;
    const accumulator = { questions: {}, screeningQuestions: {} };

    // legacy screeners live outside the unified questions list and are
    // bucketed separately so mentions can offer them as previous nodes;
    // unified-list screening questions stay in the questions bucket, where
    // previousNodesPerNode already covers them
    outsideListScreeners.forEach((question) => {
      index += 1;

      accumulator.screeningQuestions[question.refId] = enrichNodeForMentions(
        question,
        index,
      );
    });

    questions.forEach((question) => {
      index += 1;

      accumulator.questions[question.refId] = enrichNodeForMentions(
        question,
        index,
      );

      const { questions: subNodes = [] } = question;
      subNodes.forEach((subNode) => {
        index += 1;

        accumulator.questions[subNode.refId] = enrichNodeForMentions(
          subNode,
          index,
        );
      });
    });

    return accumulator;
  },
);

const makeSelectQuestionsForCarryForward = createSelector(
  makeSelectExtendedSurveyDetails,
  (questions = []) =>
    questions.map(
      ({
        refId,
        questionIndex,
        prompt,
        type,
        answers,
        scorepoints,
        carryForward = [],
        min,
        max,
        label,
        matrix,
        isScreening,
      }) => ({
        refId,
        questionIndex,
        prompt,
        type,
        answers,
        scorepoints,
        carryForward,
        hasCarryForwardConfiguration: carryForward.length > 0,
        min,
        max,
        label,
        matrix,
        isScreening,
      }),
    ),
);
const makeSelectScreeningQuestionsForCarryForward = createSelector(
  makeSelectScreeningQuestionsOutsideQuestionsList,
  (screeningQuestions = []) =>
    screeningQuestions.map(
      ({
        refId,
        questionIndex,
        prompt,
        type,
        answers,
        scorepoints,
        carryForward = [],
        min,
        max,
        label,
      }) => ({
        refId,
        questionIndex,
        prompt,
        type,
        answers,
        scorepoints,
        carryForward,
        hasCarryForwardConfiguration: carryForward.length > 0,
        min,
        max,
        label,
      }),
    ),
);

const makeSelectAllQuestionsForCarryForward = createSelector(
  makeSelectQuestionsForCarryForward,
  makeSelectScreeningQuestionsForCarryForward,
  (questions, screeningQuestions) => [...questions, ...screeningQuestions],
);

const makeSelectAllQuestionWithRecallPrompts = createSelector(
  makeSelectExtendedSurveyDetails,
  makeSelectScreeningQuestionsOutsideQuestionsList,
  (questions = [], screeningQuestions = []) =>
    [...questions, ...screeningQuestions].map(({ prompt, htmlPrompt }) => ({
      prompt,
      htmlPrompt,
    })),
);

const makeSelectQuestionsWithCarryForwardsFromScreeners = createSelector(
  makeSelectQuestionsForCarryForward,
  makeSelectScreeningQuestions,
  (questions, screeningQuestions) => {
    const screeningQuestionIds = flatMap(screeningQuestions, 'refId');
    return questions.filter((question) => {
      const { carryForward = [] } = question;
      const hasCarryForwardAnswersFromScreener = find(
        carryForward,
        (carryForwardElement) =>
          screeningQuestionIds.includes(carryForwardElement.questionRefId),
      );
      return hasCarryForwardAnswersFromScreener;
    });
  },
);

const makeSelectQuestionsWithMentionsFromScreeners = createSelector(
  makeSelectExtendedSurveyDetails,
  makeSelectScreeningQuestions,
  (questions, screeningQuestions) => {
    const screeningQuestionIds = flatMap(screeningQuestions, 'refId');
    return questions.filter((question) =>
      screeningQuestionIds.some((refId) => hasMentionOnRefId(refId)(question)),
    );
  },
);

const makeSelectSurveyId = createSelector(
  selectQuestionnairePageDomain,
  (substate) => get(substate, 'surveyDetailsExtended.id', null),
);

const makeSelectSurveyProperty = (property) =>
  createSelector(selectQuestionnairePageDomain, (substate) =>
    get(substate, `surveyDetailsExtended[${property}]`, null),
  );

const makeSelectErrors = createSelector(
  selectQuestionnairePageDomain,
  (substate) => get(substate, 'errors'),
);
const makeSelectDoeErrors = createSelector(
  selectQuestionnairePageDomain,
  (substate) => get(substate, 'doeErrors', []),
);

const makeSelectSurveyHash = createSelector(
  selectQuestionnairePageDomain,
  (substate) => get(substate, 'surveyDetailsExtended.hash', null),
);

const makeSelectQuestionnaireErrors = createSelector(
  selectQuestionnairePageDomain,
  (substate) => get(substate, 'errors', []),
);

const makeSelectPristineTimestamp = createSelector(
  selectQuestionnairePageDomain,
  (substate) => get(substate, 'surveyDetailsExtended.pristineTimestamp'),
);

const makeSelectPreviewTimestamp = createSelector(
  selectQuestionnairePageDomain,
  (substate) =>
    get(substate, 'surveyDetailsExtended.surveyPreviewTimestamp', 0),
);
const makeSelectEmptyQuestionnaire = createSelector(
  selectQuestionnaireQuestions,
  (questionnaireQuestions) => questionnaireQuestions.length === 0,
);

const makeSelectIsEmptyRegularQuestions = createSelector(
  makeSelectNonScreeningQuestions,
  (surveyQuestions) => surveyQuestions.length === 0,
);

const makeSelectActiveGroup = createSelector(
  selectQuestionnairePageDomain,
  (substate) => get(substate, 'activeGroup', null),
);

const makeSelectGroupsShuffling = createSelector(
  selectQuestionnairePageDomain,
  (substate) => get(substate, 'questionGroupShuffle', 0),
);
const makeSelectRandomizeQuestionnaireEnabled = createSelector(
  makeSelectGroupsShuffling,
  (shuffleType) => shuffleType !== SHUFFLE_NONE,
);

const makeSelectModals = createSelector(
  selectQuestionnairePageDomain,
  (substate) => substate.modals,
);

const makeSelectCanAddAbTest = createSelector(
  selectQuestionnaireQuestions,
  (nodes) => !nodes.some(isAbGroupNode),
);

const makeSelectHasConjointGroup = createSelector(
  selectQuestionnaireQuestions,
  (nodes) => nodes.some(isConjointGroupNode),
);

const makeSelectHasMaxDiffGroup = createSelector(
  selectQuestionnaireQuestions,
  (nodes) => nodes.some(isMaxDiffGroupNode),
);

const makeSelectCanAddConjoint = createSelector(
  makeSelectHasConjointGroup,
  makeSelectHasMaxDiffGroup,
  (hasConjointGroup, hasMaxDiffGroup) => !(hasConjointGroup || hasMaxDiffGroup),
);

const makeSelectCanAddVanWestendorp = createSelector(
  makeSelectActiveQuestionInternal,
  (activeQuestion) =>
    !activeQuestion ||
    Boolean(activeQuestion.groupRefId) === false ||
    isAbGroupNode(activeQuestion),
);

const makeSelectConjointSuggestedCompletes = createSelector(
  selectQuestionnaireQuestions,
  (nodes) => {
    const conjointOrMaxDiffNode = nodes.find(isConjointOrMaxDiffGroupNode);
    if (!conjointOrMaxDiffNode) {
      return 0;
    }
    const attributeWithTheMostLevels = maxBy(
      conjointOrMaxDiffNode.attributes,
      (attribute) => attribute.values.length,
    );

    // multiplier is 750 up to 1000 for larger projects
    const multiplier = 1000;
    const maxNumberOfLevels = get(
      attributeWithTheMostLevels,
      'values.length',
      1,
    );
    const repetitions = get(conjointOrMaxDiffNode, 'properties.repetitions', 1);
    const choiceSetSize = get(
      conjointOrMaxDiffNode,
      'properties.choiceSetSize',
      1,
    );

    const suggestedNumberOfCompletes =
      (multiplier * maxNumberOfLevels) / (repetitions * choiceSetSize);
    return Math.floor(suggestedNumberOfCompletes);
  },
);

const makeSelectLoopAndMergeGroups = createSelector(
  selectQuestionnaireQuestions,
  (nodes) => nodes.filter(isLoopAndMergeGroupNode),
);

const makeSelectIsDoeInvalidated = createSelector(
  makeSelectHasConjointGroup,
  makeSelectHasMaxDiffGroup,
  selectQuestionnaireQuestions,
  (hasConjointGroup, hasMaxDiffGroup, nodes) => {
    if (!(hasConjointGroup || hasMaxDiffGroup)) {
      return false;
    }
    // we currently only support one conjoint or one maxdiff group so this is fine
    const conjointOrMaxDiffGroup = nodes.find(isConjointOrMaxDiffGroupNode);
    const currentConjointVersion = get(
      conjointOrMaxDiffGroup,
      'properties.doeStructureVersion',
      -1,
    );
    const lastSuccessDoeVersion = get(
      conjointOrMaxDiffGroup,
      'properties.lastSuccessDoeVersion',
      -1,
    );

    return currentConjointVersion !== lastSuccessDoeVersion;
  },
);

const makeSelectCanAddMaxDiff = createSelector(
  makeSelectHasConjointGroup,
  makeSelectHasMaxDiffGroup,
  (hasConjointGroup, hasMaxDiffGroup) => !(hasConjointGroup || hasMaxDiffGroup),
);

const makeSelectConversationalAICountLimitReached = createSelector(
  makeSurveyIsUnderEdit,
  selectQuestionnaireQuestions,
  (isUnderEdit, nodes) => {
    if (isUnderEdit) {
      return true;
    }

    return (
      nodes.filter(isConversationalNode).length >=
      MAX_CONVERSATIONAL_AI_QUESTIONS
    );
  },
);

const makeSelectQuestionsLength = createSelector(
  makeSelectExtendedSurveyDetails,
  (nodes = []) => {
    const questionsLength = nodes.reduce(
      (accumulator, node) => {
        if (node.questions) {
          if (node.type === GROUP_AB_TEST) {
            const repetitions = get(node, 'properties.repetitions', 1);
            const numberOfQuestions = node.questions.length * repetitions;
            accumulator.ab += numberOfQuestions;
            accumulator.total += numberOfQuestions;
          } else if (node.type === GROUP_CONJOINT) {
            // repetitions is the blockSize on doe response
            accumulator.conjoint += node.properties.repetitions;
          } else if (node.type === GROUP_MAXDIFF) {
            accumulator.maxdiff += node.properties.repetitions;
          } else if (node.type === GROUP_VAN_WESTENDORP) {
            const numberOfQuestions = node.questions.length;
            accumulator.vanWestendorp += numberOfQuestions;
            accumulator.total += numberOfQuestions;
          } else {
            accumulator.total += node.questions.length;
          }
        } else {
          accumulator.total += 1;
        }

        return accumulator;
      },
      { total: 0, ab: 0, vanWestendorp: 0, conjoint: 0, maxdiff: 0 },
    );

    return questionsLength;
  },
);

const makeSelectTargeting = createSelector(
  selectQuestionnairePageDomain,
  (substate) => substate.targeting || {},
);

const makeSelectDemographics = createSelector(
  makeSelectTargeting,
  makeSelectIsDistributionLink,
  (targeting = {}, isDistributionLink) => {
    if (!isDistributionLink) {
      const { demographics = [] } = targeting;

      return demographics.filter(
        (currentDemographic) => currentDemographic.key !== 'dg_year_of_birth',
      );
    }

    return get(targeting, 'demographics', []);
  },
);

const makeSelectLastSuccessfulVersion = createSelector(
  selectQuestionnairePageDomain,
  (substate) => substate.lastSuccessfulVersion || 0,
);

const makeSelectHasDisqualificationRules = createSelector(
  selectQuestionnaireDisqualificationLogic,
  (substate) => {
    const logic = substate || {};
    const hasNodeRules = !isEmpty(logic.nodesWithLogic);
    const hasTaxonomyRules = !isEmpty(logic.taxonomyWithLogic);

    return hasNodeRules || hasTaxonomyRules;
  },
);

const makeSelectUpdateCostVersion = createSelector(
  selectQuestionnairePageDomain,
  (substate) => get(substate, 'surveyDetailsExtended.updateCostVersion'),
);

const makeSelectHasPendingChanges = createSelector(
  makeSelectQuestionnaireEditVersion,
  makeSelectQuestionnaireSavedEditVersion,
  makeSelectIsSaving,
  (editVersion = -1, savedVersion = -1, isSaving) =>
    editVersion !== savedVersion || isSaving,
);

const makeSelectVanWestendorpQuestion = createSelector(
  makeSelectActiveQuestionInternal,
  (question) => {
    if (isVanWestendorpGroupNode(question)) {
      const vanWestendorpQuestion = find(
        question.questions,
        isVanWestendorpQuestionNode,
      );

      return vanWestendorpQuestion;
    }

    if (isVanWestendorpQuestionNode(question)) {
      return question;
    }

    return null;
  },
);

const makeSelectActiveVanWestendorpGroup = createSelector(
  makeSelectActiveQuestionInternal,
  makeSelectExtendedSurveyDetails,
  (question, questions) => {
    if (isVanWestendorpGroupNode(question)) {
      return question;
    }

    if (isVanWestendorpQuestionNode(question)) {
      return find(questions, ['refId', question.groupRefId]);
    }

    return null;
  },
);

const makeSelectPermissionsForResearchMethods = createSelector(
  makeSelectAccountPerks,
  makeSelectCanAddAbTest,
  makeSelectCanAddConjoint,
  makeSelectCanAddMaxDiff,
  makeSelectCanAddVanWestendorp,
  makeSelectEmptyQuestionnaire,
  (
    accountPerks,
    canAddAbTest,
    canAddConjoint,
    canAddMaxDiff,
    canAddVanWestendorp,
    emptyQuestions,
  ) => {
    const canEnableAbTest = get(accountPerks, 'abTest', false);
    const canEnableConjoint = get(accountPerks, 'conjointTest', false);
    const canEnableMaxDiff = get(accountPerks, 'maxDiff', false);
    const canEnableVanWestendorp = get(
      accountPerks,
      'vanWestendorpQuestion',
      false,
    );

    return {
      canEnable: {
        [GROUP_AB_TEST]: canEnableAbTest,
        [GROUP_CONJOINT]: canEnableConjoint,
        [GROUP_MAXDIFF]: canEnableMaxDiff,
        [GROUP_VAN_WESTENDORP]: canEnableVanWestendorp,
      },
      canAdd: {
        [GROUP_AB_TEST]: emptyQuestions || canAddAbTest,
        [GROUP_CONJOINT]: emptyQuestions || canAddConjoint,
        [GROUP_MAXDIFF]: emptyQuestions || canAddMaxDiff,
        [GROUP_VAN_WESTENDORP]: emptyQuestions || canAddVanWestendorp,
      },
    };
  },
);

const makeSelectPermissionsForResearchMethodsActiveNode = createSelector(
  makeSelectPermissionsForResearchMethods,
  makeSelectActiveQuestion,
  (permissionsForResearchMethods, activeNode = {}) => ({
    ...permissionsForResearchMethods,
    canChange: {
      ...permissionsForResearchMethods.canAdd,
      [GROUP_CONJOINT]:
        permissionsForResearchMethods.canAdd[GROUP_CONJOINT] ||
        isMaxDiffGroup(activeNode.type),
      [GROUP_MAXDIFF]:
        permissionsForResearchMethods.canAdd[GROUP_MAXDIFF] ||
        isConjointGroup(activeNode.type),
    },
  }),
);

const makeSelectTheme = createSelector(
  selectQuestionnairePageDomain,
  (questionnaire) => get(questionnaire, 'surveyDetailsExtended.theme', {}),
);

const makeSelectIsPollfishTheme = createSelector(makeSelectTheme, (theme) =>
  get(theme, 'isPollfish', false),
);

const makeSelectHasChangedTheme = createSelector(
  makeSelectTheme,
  (theme) => !get(theme, 'isDefault', true),
);

const makeSelectHasSurveyThemeError = createSelector(
  makeSelectAccountPerks,
  makeSelectIsPollfishTheme,
  makeSelectActiveTheme,
  (accountPerks, isPollfishTheme, activeTheme) => {
    const hasPermission = hasPerkSurveyTheming({ perks: accountPerks });
    if (hasPermission) {
      return false;
    }
    return !activeTheme.isPollfish && !isPollfishTheme;
  },
);

const makeSelectIsActiveNewNode = createSelector(
  selectQuestionnairePageDomain,
  (questionnaire) => get(questionnaire, 'isActiveNewNode', false),
);

const makeSelectIsQuestionnairePreview = createSelector(
  selectQuestionnairePageDomain,
  (substate) =>
    get(substate, 'surveyDetailsExtended.isQuestionnairePreview', false),
);

const makeSelectExternalUpdateTimestamp = createSelector(
  selectQuestionnairePageDomain,
  (substate) => get(substate, 'externalUpdateTimestamp'),
);

const makeSelectVariablesWithLogic = createSelector(
  selectVariablesWithLogic,
  selectVariablesWithDisqualificationLogic,
  (
    variablesWithQuestionnaireLogic = {},
    variablesWithDisqualificationLogic = {},
  ) => ({
    variablesWithQuestionnaireLogic,
    variablesWithDisqualificationLogic,
  }),
);

const makeSelectExternalUpsertAdditionalInfo = createSelector(
  selectQuestionnairePageDomain,
  (substate) => get(substate, 'externalUpsertAdditionalInfo', {}),
);

const makeSelectHasConversationalPerk = createSelector(
  makeSelectAccountPerks,
  (accountPerks) => {
    const hasPermission = hasPerkConversational({ perks: accountPerks });
    return hasPermission;
  },
);

const makeSelectStatementIdToScroll = createSelector(
  selectQuestionnairePageDomain,
  (substate = []) => get(substate, 'statementIdToScroll'),
);

const makeSelectQuestionsDiff = createSelector(
  selectQuestionsDiff,
  (substate) => substate,
);

const makeSelectLoopAndMergeGroupsBasedOnSurveyStatus = createSelector(
  makeSurveyIsDraft,
  makeSelectLoopAndMergeGroups,
  (isDraft, loopAndMergeGroups) => (isDraft ? loopAndMergeGroups : []),
);

const makeSelectIsFinalisedUnderEdit = createSelector(
  selectQuestionnairePageDomain,
  (substate) =>
    !!get(substate, 'surveyDetailsExtended.isNotFinalisedUnderEdit', false),
);

const makeSelectHasDifferencesInQuestionnaire = createSelector(
  selectQuestionnairePageDomain,
  (substate) =>
    get(substate, 'surveyDetailsExtended.hasDifferencesInQuestionnaire', false),
);

const makeSelectHasDifferencesInLinkSettings = createSelector(
  selectQuestionnairePageDomain,
  (substate) =>
    get(substate, 'surveyDetailsExtended.hasDifferencesInLinkSettings', false),
);

const makeSelectQuestions = createSelector(
  selectQuestionnaireQuestions,
  (questions) => questions,
);

export {
  selectQuestionnaireActiveQuestionId,
  makeSelectNodesWithHiddenAnswerReferences,
  makeSelectAdlBlockingErrorSections,
  selectQuestionnaireQuestions,
  makeSelectQuestionnairePage,
  makeSelectQuestionnaireEditVersion,
  makeSelectQuestionnaireSavedEditVersion,
  makeSelectExtendedSurveyDetails,
  makeSelectActiveQuestionId,
  makeSelectActiveQuestion,
  makeSelectActiveQuestionSettings,
  makeSelectQuestionsForMentions,
  makeSelectSurveyId,
  makeSelectSurveyProperty,
  makeSelectErrors,
  makeSelectLogicErrors,
  makeSelectSurveyHash,
  makeSelectQuestionnaireErrors,
  makeSelectPristineTimestamp,
  makeSelectPreviewTimestamp,
  makeSelectNodesWithLogic,
  makeSelectNodesWithNonScreeningLogic,
  makeSelectNodesWithSimpleQuotaOnlyLogic,
  makeSelectNodesWithAdvancedQuotaOnlyLogic,
  makeSelectTaxonomyWithLogic,
  makeSelectTaxonomyWithQuestionnaireLogic,
  makeSelectAnswersWithLogic,
  makeSelectAnswersWithNonScreeningLogic,
  makeSelectConceptsWithLogic,
  makeSelectAnswersWithOriginLogic,
  makeSelectSurveyLogicToStore,
  makeSelectEmptyQuestionnaire,
  makeSelectScreeningQuestions,
  makeSelectQuestionnaireGroups,
  makeSelectQuestionnaireLoading,
  makeSelectQuestionnaireSaving,
  makeSelectActiveGroup,
  makeSelectGroupsShuffling,
  makeSelectMaxQlPath,
  makeSelectHasTooManyQuestions,
  makeSelectHasReachedQuestionLimit,
  makeSelectRecallRequest,
  makeSelectQuestionsForCarryForward,
  makeSelectScreeningQuestionsForCarryForward,
  makeSelectAllQuestionsForCarryForward,
  makeSelectTranslatedNodes,
  makeSelectModals,
  makeSelectCanAddAbTest,
  makeSelectCanAddConjoint,
  makeSelectCanAddVanWestendorp,
  makeSelectHasConjointGroup,
  makeSelectIsDoeInvalidated,
  makeSelectConjointSuggestedCompletes,
  makeSelectCanAddMaxDiff,
  makeSelectQuestionsLength,
  makeSelectNodesForTranslations,
  makeSelectRandomizeQuestionnaireEnabled,
  makeSelectTargeting,
  makeSelectLastSuccessfulVersion,
  makeSelectUpdateCostVersion,
  makeSelectHasFetched,
  makeSelectQuestionsWithCarryForwardsFromScreeners,
  makeSelectQuestionsWithMentionsFromScreeners,
  makeSelectIsSaving,
  makeSelectIsFetchingConjointSettings,
  makeSelectHasPendingChanges,
  makeSelectHasDisqualificationRules,
  makeSelectVanWestendorpQuestion,
  makeSelectActiveVanWestendorpGroup,
  makeSelectQuestionLimit,
  makeSelectDemographics,
  makeSelectPermissionsForResearchMethods,
  makeSelectPermissionsForResearchMethodsActiveNode,
  makeSelectAllQuestionWithRecallPrompts,
  makeSelectHasChangedTheme,
  makeSelectHasSurveyThemeError,
  makeSelectIsPollfishTheme,
  makeSelectFormattedNodes,
  makeSelectScreeningQuestionsLength,
  makeSelectCanAddMoreScreeningQuestions,
  makeSelectScreeningLogicEnabled,
  makeSelectShouldEnforceScreeningBoundary,
  makeSelectPermissionsForScreeningQuestions,
  makeSelectIsEmptyRegularQuestions,
  makeSelectMaxAllowedScreeningQuestions,
  makeSelectIsActiveNewNode,
  makeSelectQuestionnaireRevisions,
  makeSelectIsQuestionnaireInRevisionPreviewMode,
  makeSelectIsQuestionnairePreview,
  makeSelectExternalUpdateTimestamp,
  makeSelectVariablesWithLogic,
  makeSelectExternalUpsertAdditionalInfo,
  makeSelectHasConversationalPerk,
  makeSelectAnswersWithLogicFromOutcomes,
  makeSelectAnswersWithLogicFromOutcomesShow,
  makeSelectAnswersWithLogicFromOutcomesHide,
  makeSelectStatementIdToScroll,
  makeSelectConversationalAICountLimitReached,
  makeSelectQuestionsDiff,
  makeSelectLoopAndMergeGroups,
  makeSelectLoopAndMergeGroupsBasedOnSurveyStatus,
  makeSelectIsFinalisedUnderEdit,
  makeSelectHasDifferencesInQuestionnaire,
  makeSelectHasDifferencesInLinkSettings,
  makeSelectHiddenNodes,
  makeSelectNodesWithLogicFromOutcomesShow,
  makeSelectRulesWithQuotas,
  makeSelectActiveQuestionHasAdvancedQuotas,
  makeSelectQuestions,
  makeSelectDoeErrors,
  makeSelectLogicPaths,
  makeSelectLogicPathsWithoutScreeningNavigation,
};
